/**
 * What a live recording needs from the host, as types the core can hold.
 *
 * ## Why this is not the export pipeline
 *
 * Phase 7 built a `VideoEncoder`/muxer pipeline and it is the right tool for
 * what it does: rendering a performance again, offline, as fast as the machine
 * allows, with every timestamp derived rather than measured. None of that suits
 * recording a live run. Offline export owns the clock; a live recording is
 * *subordinate* to a clock somebody is playing against, and the one thing it
 * must never do is make the game wait for it.
 *
 * `MediaRecorder` is built for exactly this: the browser encodes on its own
 * threads, drops what it cannot keep up with, and hands back finished chunks.
 * Pushing frames through WebCodecs by hand during play would put encoder
 * backpressure on the render loop — which is the one place in this application
 * where a stall is a scoring error rather than a stutter.
 *
 * So the two are separate on purpose, and this file holds none of the export's
 * types.
 *
 * ## The three things that are not the same
 *
 * `SAVE REPLAY JSON` writes what the player did. `RECORD` writes what the
 * screen showed. `RE-RENDER VIDEO` draws the performance again at whatever
 * quality is asked for. A user who confuses them loses work, so the vocabulary
 * here never says "export" or "replay".
 */

// ---------------------------------------------------------------------------
// The recorder, as the core sees it
// ---------------------------------------------------------------------------

export type RecorderState = 'inactive' | 'recording' | 'paused';

/**
 * A chunk of encoded media, as much of it as the core needs to keep.
 *
 * Opaque: on a real host this is a `Blob`, and the core's only jobs are to
 * count them, add up their sizes, and hand the collection back at the end.
 */
export interface RecordedChunk {
  readonly size: number;
}

export interface RecorderAdapter {
  readonly state: RecorderState;
  /** The MIME the host actually chose, which may differ from what was asked. */
  readonly mimeType: string;
  start(timesliceMs?: number): void;
  stop(): void;
  dispose(): void;
}

export interface RecorderCallbacks {
  readonly onChunk: (chunk: RecordedChunk) => void;
  /** Fired once, after the final chunk, when the host has finished. */
  readonly onStop: () => void;
  readonly onError: (message: string) => void;
}

/**
 * The stream being recorded, and how to let go of it.
 *
 * `release` is separate from the recorder's own disposal because the tracks
 * outlive it: a canvas capture track keeps the compositor doing extra work for
 * as long as it is live, whether or not anything is reading it.
 */
export interface RecordingSource {
  readonly hasVideo: boolean;
  readonly hasAudio: boolean;
  readonly width: number;
  readonly height: number;
  readonly fps: number;
  release(): void;
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export const RECORD_FPS = [30, 60] as const;
export type RecordFps = (typeof RECORD_FPS)[number];

export interface LiveRecordingConfig {
  readonly fps: RecordFps;
  readonly audio: boolean;
  /** Starts and stops with the run rather than with a button. */
  readonly autoRecord: boolean;
}

export const DEFAULT_RECORDING_CONFIG: LiveRecordingConfig = {
  fps: 60,
  audio: true,
  autoRecord: false,
};

// ---------------------------------------------------------------------------
// MIME selection
// ---------------------------------------------------------------------------

export interface MimeCandidate {
  readonly mime: string;
  /** What to show the user, and the extension the file gets. */
  readonly label: string;
  readonly extension: 'webm' | 'mp4';
}

/**
 * The formats worth trying, best first.
 *
 * WebM before MP4, which is the opposite of the offline export's preference and
 * is deliberate. `MediaRecorder`'s WebM path is the one every engine has
 * implemented for a decade; its MP4 support is recent, and where it exists it is
 * more likely to produce a file with an unusual layout. Offline export can
 * afford to prefer MP4 because it muxes the container itself and can verify the
 * result; a live recording takes whatever the browser gives it, so the safer
 * container wins.
 *
 * VP9 before VP8 for size at the same quality; a bare `video/webm` last, because
 * leaving the codec unstated lets the browser pick and at least produces a file.
 */
export const RECORDING_MIMES: readonly MimeCandidate[] = [
  { mime: 'video/webm;codecs=vp9,opus', label: 'WebM · VP9 + Opus', extension: 'webm' },
  { mime: 'video/webm;codecs=vp8,opus', label: 'WebM · VP8 + Opus', extension: 'webm' },
  { mime: 'video/webm', label: 'WebM', extension: 'webm' },
  { mime: 'video/mp4;codecs=avc1.42E01E,mp4a.40.2', label: 'MP4 · H.264 + AAC', extension: 'mp4' },
  { mime: 'video/mp4', label: 'MP4', extension: 'mp4' },
];

/** Video-only variants, for a recording with the sound switched off. */
export const RECORDING_MIMES_SILENT: readonly MimeCandidate[] = [
  { mime: 'video/webm;codecs=vp9', label: 'WebM · VP9', extension: 'webm' },
  { mime: 'video/webm;codecs=vp8', label: 'WebM · VP8', extension: 'webm' },
  { mime: 'video/webm', label: 'WebM', extension: 'webm' },
  { mime: 'video/mp4', label: 'MP4', extension: 'mp4' },
];

export type MimeProbe = (mime: string) => boolean;

/**
 * Picks a format the browser will actually record.
 *
 * Asked rather than assumed, for the same reason the offline export asks about
 * codecs: a fixed string fails outright on a browser without that muxer, and the
 * failure arrives as an exception when the user presses record rather than as a
 * greyed-out control.
 */
export function selectRecordingMime(
  probe: MimeProbe,
  options: { readonly audio: boolean } = { audio: true },
): MimeCandidate | null {
  const candidates = options.audio ? RECORDING_MIMES : RECORDING_MIMES_SILENT;
  for (const candidate of candidates) {
    try {
      if (probe(candidate.mime)) return candidate;
    } catch {
      // A probe that throws is a "no"; some engines reject unknown strings
      // rather than answering false.
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export const RECORDING_STATES = [
  'IDLE',
  'READY',
  'RECORDING',
  'STOPPING',
  'DONE',
  'CANCELLED',
  'FAILED',
] as const;
export type RecordingState = (typeof RECORDING_STATES)[number];

export type RecordingFailure =
  | 'no-recorder'
  | 'no-capture'
  | 'no-audio-stream'
  | 'no-mime'
  | 'start-failed'
  | 'recorder-error'
  | 'no-data'
  | 'unknown';

const FAILURE_TEXT: Readonly<Record<RecordingFailure, string>> = {
  'no-recorder': 'This browser cannot record: MediaRecorder is unavailable.',
  'no-capture': 'The playfield canvas cannot be captured in this browser.',
  'no-audio-stream': 'The song could not be routed into the recording. Turn Audio off to record video only.',
  'no-mime': 'No recording format this browser supports could be found.',
  'start-failed': 'The recorder refused to start.',
  'recorder-error': 'The recorder stopped with an error.',
  'no-data': 'The recording finished with no data.',
  unknown: 'Recording stopped for an unknown reason.',
};

export function describeRecordingFailure(kind: RecordingFailure, detail?: string): string {
  const base = FAILURE_TEXT[kind];
  return detail === undefined || detail.length === 0 ? base : `${base} (${detail})`;
}

// ---------------------------------------------------------------------------
// Naming
// ---------------------------------------------------------------------------

/**
 * A file name that says this is a live capture.
 *
 * `_live_` is in the middle on purpose: a downloads folder holding both these
 * and the offline exports should sort them together by song and still let
 * somebody tell at a glance which is which.
 */
export function recordingFileName(options: {
  readonly song: string;
  readonly extension: string;
  readonly at: Date;
}): string {
  const song = sanitise(options.song) || 'mandalizm';
  const at = options.at;
  const pad = (value: number): string => String(value).padStart(2, '0');
  const stamp =
    `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}` +
    `_${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`;
  return `mandalizm_live_${song}_${stamp}.${options.extension}`;
}

function sanitise(value: string): string {
  return value
    // eslint-disable-next-line no-control-regex
    .replace(/[<>:"/\\|?* -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/^[.\-]+|[.\-]+$/g, '')
    .slice(0, 60);
}

/** `0:18`, for a counter somebody is watching tick. */
export function elapsedLabel(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** `4.2 MB`, matching the export panel's phrasing. */
export function recordingSizeLabel(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1_000_000) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}
